import { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useUrlState, useDebounce, compareFilters, isDefaultFilters } from '@/shared/lib';
import { SERVICE_REQUEST_STATUS } from '@/entities/service-request';
import type { ActiveFilterChip } from '@/features/header-filters';

import type { ServiceRequestUrlState } from '../model/types';
import { getServiceRequestStatusOptions } from '../model/filter-options';
import { DEFAULT_URL_STATE } from './constants';

export const useServiceRequestsFilters = () => {
  const { t } = useTranslation();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [localFilters, setLocalFilters] = useState<ServiceRequestUrlState>(() => urlState);
  const [searchQuery, setSearchQuery] = useState(() => urlState.search);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const activeFiltersCount = useMemo(() => {
    let count = 0;

    if (urlState.status && urlState.status !== SERVICE_REQUEST_STATUS.ALL) {
      count++;
    }

    return count;
  }, [urlState]);

  const hasActiveFilters = activeFiltersCount > 0;

  const isFiltersDirty = useMemo(() => {
    return !compareFilters(localFilters, urlState, ['search']);
  }, [localFilters, urlState]);

  const hasDefaultFilters = useMemo(() => {
    return isDefaultFilters(urlState, DEFAULT_URL_STATE, ['search']);
  }, [urlState]);

  const handleFilterChange = useCallback((key: keyof ServiceRequestUrlState, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleReset = useCallback(() => {
    setLocalFilters(DEFAULT_URL_STATE);
    setSearchQuery('');
    resetUrlState();
  }, [resetUrlState]);

  const removeFilter = useCallback(
    (key: string) => {
      setUrlState({ [key]: DEFAULT_URL_STATE[key as keyof ServiceRequestUrlState] });
    },
    [setUrlState]
  );

  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    if (!urlState.status || urlState.status === SERVICE_REQUEST_STATUS.ALL) {
      return [];
    }
    const label = getServiceRequestStatusOptions(t).find((o) => o.value === urlState.status)?.label ?? urlState.status;
    return [{ key: 'status', label, onRemove: () => removeFilter('status') }];
  }, [urlState.status, t, removeFilter]);

  const handleApplyFilters = useCallback(() => {
    const { search, ...filtersWithoutSearch } = localFilters;
    setUrlState(filtersWithoutSearch);
  }, [localFilters, setUrlState]);

  useEffect(() => {
    setLocalFilters(urlState);
  }, [urlState]);

  useEffect(() => {
    if (debouncedSearchQuery !== urlState.search) {
      setUrlState({ search: debouncedSearchQuery });
    }
  }, [debouncedSearchQuery, setUrlState, urlState.search]);

  return {
    // State
    urlState: localFilters,
    activeFilters,
    searchQuery,
    activeFiltersCount,
    hasActiveFilters,
    isFiltersDirty,
    hasDefaultFilters,

    // Handlers
    handleFilterChange,
    handleSearchChange,
    handleReset,
    handleApplyFilters
  };
};
